/**
 * Event content for a self-schedule picked slot. Time label top-left
 * (ellipsised if the pill is narrow) with a × pinned to the
 * top-right so it is visible regardless of slot height or label
 * width.
 */
import { Box, Flex, IconButton } from '@chakra-ui/react';
import { XClose } from '@kula-ai/icons-core';
import moment from 'moment-timezone';
import { KText } from '@/ui-library/components/text';
import type { SlotOverrideType } from '../types';

interface SlotPillProps {
  slotId: string;
  startIso: string;
  endIso: string;
  timezone: string;
  /** Origin — forwarded to the reducer so it knows whether to stash
   *  the id in `droppedIds` or drop from `customPicks`. */
  isGenerated: boolean;
  /** "Send additional slots" flow — candidate has already booked this
   *  slot. Hides the × and applies a confirmed-style label. */
  isBooked?: boolean;
  /** Title rendered on the booked-slot variant. Mirrors manual-mode
   *  `BookedEventContent` so confirmed picks read the same in both
   *  scheduling flows. Falls back to "Interview" when absent. */
  interviewName?: string;
  /**
   * Backend override classification (`is_custom`/`override_type`).
   * When non-`'none'` we wrap the pill in a "Custom slot" tooltip so
   * the recruiter sees the slot is overriding a constraint (busy
   * event, working hours, or both). Mirrors legacy
   * `SelfFreeSlot.tsx`'s tooltip behaviour. Currently set in two
   * places: `seedFromInitial` (server-sourced slots) and
   * `payload/computeSlotOverride.ts` at save time — but the latter
   * runs only when the user clicks "Schedule", so live drag/click
   * picks won't show this tooltip until they're persisted then
   * re-loaded. That's a UX gap to revisit if recruiters ask for a
   * live indicator. See `docs/SELF_SCHEDULE_BEHAVIOR.md`.
   */
  overrideType?: SlotOverrideType;
  onRemove: (_slotId: string, _isGenerated: boolean) => void;
}

export function SlotPill({
  slotId,
  startIso,
  endIso,
  timezone,
  isGenerated,
  isBooked = false,
  interviewName,
  onRemove,
}: SlotPillProps) {
  const label = moment.tz(startIso, timezone).format('hh:mm A');
  const endLabel = moment.tz(endIso, timezone).format('hh:mm A');

  // Booked-slot layout mirrors the manual-mode `BookedEventContent`:
  // title (interview name) on top, time range below, both inheriting
  // the pill's white text on the solid primary background.
  if (isBooked) {
    const title = interviewName || 'Interview';
    return (
      <Box h="100%" w="100%" px="6px" pt="4px" overflow="hidden">
        <KText
          fontSize="11px"
          fontWeight="500"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          lineHeight="14px"
          color="inherit"
        >
          {title}
        </KText>
        <KText
          fontSize="10px"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          lineHeight="12px"
          color="inherit"
          opacity={0.9}
        >
          {`${label} – ${endLabel}`}
        </KText>
      </Box>
    );
  }

  // Time label + × on a single space-between row (Figma). The label
  // takes the free space and ellipsises; the × stays a fixed 16px so
  // it's always visible regardless of pill width or height.
  const content = (
    <Flex
      h="100%"
      w="100%"
      px="6px"
      pt="6px"
      gap="6px"
      align="flex-start"
      justify="space-between"
      overflow="hidden"
    >
      <KText
        fontSize="12px"
        fontWeight="600"
        lineHeight="16px"
        color="primary-700"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        flex="1"
        minW={0}
      >
        {label}
      </KText>
      <IconButton
        aria-label={`Remove slot ${label}–${endLabel}`}
        icon={<XClose size={16} />}
        size="xs"
        variant="ghost"
        minW="16px"
        w="16px"
        h="16px"
        p={0}
        flexShrink={0}
        color="primary-700"
        _hover={{ bg: 'primary-200' }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove(slotId, isGenerated);
        }}
      />
    </Flex>
  );

  return content;
}
